import { Badge, Box, Group, Loader, Stack, Text } from '@mantine/core';
import { MessageSquare, Star } from 'react-feather';
import { ContentCard } from '../../../../components/common/ContentCard';

/**
 * MED-1193 (B9) — the session discussion-themes card, rendered on all three
 * post-session views. The bundle delivers ONLY the viewing audience's register
 * (coach analytic vs plain — AC5 no-cross-leak is enforced server-side); this
 * card just renders what it's given, with the register-appropriate section
 * title ("Discussion Themes" for the coach, "What the group talked about" for
 * session leaders and participants).
 *
 * Availability follows the B7 card pattern: absent entirely when AI wasn't
 * active (envelope null / aiAvailable false); loader while the themes row is
 * still computing; and — per the 0-floor rule (Ronnie 7/13) — when nothing
 * cleared the theme bar the card does NOT render at all (no filler, no empty
 * state): the envelope reports not_available and we return null.
 */

const BAND_DIVIDER = '#D1D5DB';
const VALUE_COLOR = '#374151';
const LABEL_COLOR = '#6B7280';
const SUBCARD_BORDER = '#E5E7EB';

export interface SessionThemesEnvelope {
  status: 'available' | 'pending' | 'not_available';
  themes?: Array<{ title: string; description: string }>;
}

interface Props {
  /** The bundle's themes envelope. Null = AI wasn't active (card absent). */
  themes: SessionThemesEnvelope | null;
  /** MED-1128 effective gate — modality AI && session listening. */
  aiAvailable: boolean;
  /** Which register this view shows — drives the section title only. */
  viewerRegister: 'coach' | 'plain';
}

export function SessionThemesCard({ themes, aiAvailable, viewerRegister }: Props) {
  if (!aiAvailable || themes == null) return null;
  // 0-floor: nothing cleared the bar → no card, no empty state (AC2).
  if (themes.status === 'not_available') return null;

  const title = viewerRegister === 'coach' ? 'Discussion Themes' : 'What the group talked about';
  const list = themes.status === 'available' ? (themes.themes ?? []) : [];
  if (themes.status === 'available' && list.length === 0) return null;

  return (
    <ContentCard
      variant="elevated"
      padding={0}
      contentGap={0}
      fill={false}
      header={null}
      data-testid="session-themes-card"
      style={{ overflow: 'hidden' }}
    >
      <Group
        justify="space-between"
        align="center"
        wrap="nowrap"
        style={{ padding: '16px 20px', borderBottom: `1px solid ${BAND_DIVIDER}` }}
      >
        <Group gap={8} align="center" wrap="nowrap">
          <MessageSquare size={15} color={VALUE_COLOR} />
          <Text fw={700} fz={14} c={VALUE_COLOR}>
            {title}
          </Text>
        </Group>
        {list.length > 0 && (
          <Badge
            size="sm"
            variant="light"
            leftSection={<Star size={10} />}
            styles={{
              root: {
                backgroundColor: '#f3f0ff',
                color: '#5a3eb6',
                textTransform: 'none',
                fontWeight: 500,
              },
            }}
          >
            AI-generated
          </Badge>
        )}
      </Group>

      <Stack gap={12} style={{ padding: 20 }}>
        {themes.status === 'pending' ? (
          <Group gap={8} align="center">
            <Loader size="xs" />
            <Text size="sm" c={LABEL_COLOR}>
              Gathering this session&apos;s themes…
            </Text>
          </Group>
        ) : (
          list.map((t, i) => (
            <Box
              key={i}
              style={{
                border: `1px solid ${SUBCARD_BORDER}`,
                borderRadius: 8,
                padding: '12px 16px',
              }}
            >
              <Text fw={700} fz={13} c={VALUE_COLOR} mb={4}>
                {t.title}
              </Text>
              <Text fz={13} c={VALUE_COLOR}>
                {t.description}
              </Text>
            </Box>
          ))
        )}
      </Stack>
    </ContentCard>
  );
}

export default SessionThemesCard;
